import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  ProgressBar,
  Table,
  Modal,
  Form,
  Nav,
  Tab
} from 'react-bootstrap';

// Veri tipleri
interface WhatsAppCampaign {
  id: number;
  name: string;
  status: 'active' | 'completed' | 'scheduled' | 'draft';
  template: string;
  recipients: number;
  sent: number;
  delivered: number;
  read: number;
  replied: number;
  scheduledDate?: string;
  createdAt: string;
}

interface WhatsAppTemplate {
  id: number;
  name: string;
  content: string;
  category: string;
  variables: string[];
  status: 'approved' | 'pending' | 'rejected';
  createdAt: string;
}

// Örnek veriler
const dummyCampaigns: WhatsAppCampaign[] = [
  {
    id: 1,
    name: 'Welcome Message',
    status: 'active',
    template: 'Welcome Template',
    recipients: 1000,
    sent: 950,
    delivered: 900,
    read: 800,
    replied: 200,
    createdAt: '2024-03-01T10:00:00Z',
  },
  {
    id: 2,
    name: 'Product Update',
    status: 'scheduled',
    template: 'Product Update Template',
    recipients: 500,
    sent: 0,
    delivered: 0,
    read: 0,
    replied: 0,
    scheduledDate: '2024-04-01T09:00:00Z',
    createdAt: '2024-03-20T14:30:00Z',
  },
];

const dummyTemplates: WhatsAppTemplate[] = [
  {
    id: 1,
    name: 'Welcome Template',
    content: 'Welcome to our service! We are excited to have you on board...',
    category: 'Onboarding',
    variables: ['name', 'company'],
    status: 'approved',
    createdAt: '2024-03-01T10:00:00Z',
  },
  {
    id: 2,
    name: 'Product Update Template',
    content: 'Exciting news! We have just launched our new feature...',
    category: 'Update',
    variables: ['product_name', 'feature_name'],
    status: 'pending',
    createdAt: '2024-03-20T14:30:00Z',
  },
];

const WhatsappMarketingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);
  const [showNewTemplateModal, setShowNewTemplateModal] = useState(false);

  // Durum rengini belirleyen yardımcı fonksiyon
  const getStatusVariant = (status: string): string => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'info';
      case 'scheduled':
        return 'warning';
      case 'draft':
        return 'secondary';
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  return (
    <Container fluid className="py-4">
      {/* Başlık Bölümü */}
      <Row className="mb-4">
        <Col>
          <h2 className="fw-bold mb-1">WhatsApp Marketing</h2>
          <p className="text-muted">Manage your WhatsApp campaigns, templates, and analytics</p>
        </Col>
      </Row>

      {/* İstatistik Kartları */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} md={3}>
          <Card className="h-100 border-0 shadow-sm rounded-3">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10 me-3" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-chat-text-fill fs-4 text-primary"></i>
                </div>
                <h6 className="text-muted mb-0">Total Messages</h6>
              </div>
              <h3 className="fw-bold mb-2">1,450</h3>
              <ProgressBar now={75} variant="primary" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={3}>
          <Card className="h-100 border-0 shadow-sm rounded-3">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 me-3" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-check-circle-fill fs-4 text-success"></i>
                </div>
                <h6 className="text-muted mb-0">Delivery Rate</h6>
              </div>
              <h3 className="fw-bold mb-2">94.7%</h3>
              <ProgressBar now={94.7} variant="success" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={3}>
          <Card className="h-100 border-0 shadow-sm rounded-3">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-info bg-opacity-10 me-3" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-people-fill fs-4 text-info"></i>
                </div>
                <h6 className="text-muted mb-0">Active Campaigns</h6>
              </div>
              <h3 className="fw-bold mb-2">2</h3>
              <ProgressBar now={60} variant="info" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={3}>
          <Card className="h-100 border-0 shadow-sm rounded-3">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-10 me-3" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-megaphone-fill fs-4 text-warning"></i>
                </div>
                <h6 className="text-muted mb-0">Templates</h6>
              </div>
              <h3 className="fw-bold mb-2">8</h3>
              <ProgressBar now={80} variant="warning" style={{ height: '6px', borderRadius: '3px' }} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sekmeler */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'campaigns')}>
        <Row className="mb-4">
          <Col>
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="campaigns">Campaigns</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="templates">Templates</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="analytics">Analytics</Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
        </Row>

        <Tab.Content>
          {/* Kampanyalar Sekmesi */}
          <Tab.Pane eventKey="campaigns">
            <Card className="border-0 shadow-sm rounded-3">
              <Card.Body>
                <div className="d-flex justify-content-between mb-4">
                  <Button 
                    variant="success" 
                    className="d-flex align-items-center"
                    onClick={() => setShowNewCampaignModal(true)}
                    style={{
                      background: 'linear-gradient(45deg, #25D366 30%, #128C7E 90%)',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    New Campaign
                  </Button>
                </div>
                
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Campaign Name</th>
                        <th>Status</th>
                        <th>Recipients</th>
                        <th>Sent</th>
                        <th>Delivered</th>
                        <th>Read</th>
                        <th>Replied</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dummyCampaigns.map(campaign => (
                        <tr key={campaign.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center justify-content-center rounded-circle me-2 text-white" style={{ width: '40px', height: '40px', backgroundColor: '#25D366' }}>
                                <i className="bi bi-whatsapp"></i>
                              </div>
                              <span className="fw-medium">{campaign.name}</span>
                            </div>
                          </td>
                          <td>
                            <Badge bg={getStatusVariant(campaign.status)} pill>
                              {campaign.status}
                            </Badge>
                          </td>
                          <td>{campaign.recipients}</td>
                          <td>{campaign.sent}</td>
                          <td>{campaign.delivered}</td>
                          <td>{campaign.read}</td>
                          <td>{campaign.replied}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button variant="primary" size="sm" className="rounded-circle p-1">
                                <i className="bi bi-pencil-fill"></i>
                              </Button>
                              <Button variant="danger" size="sm" className="rounded-circle p-1">
                                <i className="bi bi-trash-fill"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Şablonlar Sekmesi */}
          <Tab.Pane eventKey="templates">
            <Card className="border-0 shadow-sm rounded-3">
              <Card.Body>
                <div className="d-flex justify-content-between mb-4">
                  <Button 
                    variant="success" 
                    className="d-flex align-items-center"
                    onClick={() => setShowNewTemplateModal(true)}
                    style={{
                      background: 'linear-gradient(45deg, #25D366 30%, #128C7E 90%)',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    New Template
                  </Button>
                </div>
                
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Template Name</th>
                        <th>Content</th>
                        <th>Category</th>
                        <th>Variables</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dummyTemplates.map(template => (
                        <tr key={template.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center justify-content-center rounded-circle me-2 text-white" style={{ width: '40px', height: '40px', backgroundColor: '#25D366' }}>
                                <i className="bi bi-chat-text"></i>
                              </div>
                              <span className="fw-medium">{template.name}</span>
                            </div>
                          </td>
                          <td>
                            <div style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {template.content}
                            </div>
                          </td>
                          <td>{template.category}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {template.variables.map(variable => (
                                <Badge key={variable} bg="light" text="dark" className="rounded-pill">
                                  {variable}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td>
                            <Badge bg={getStatusVariant(template.status)} pill>
                              {template.status}
                            </Badge>
                          </td>
                          <td>{new Date(template.createdAt).toLocaleDateString()}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button variant="primary" size="sm" className="rounded-circle p-1">
                                <i className="bi bi-pencil-fill"></i>
                              </Button>
                              <Button variant="danger" size="sm" className="rounded-circle p-1">
                                <i className="bi bi-trash-fill"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Analitik Sekmesi */}
          <Tab.Pane eventKey="analytics">
            <Row className="g-4">
              <Col xs={12} md={6}>
                <Card className="border-0 shadow-sm rounded-3 h-100">
                  <Card.Body>
                    <h5 className="mb-4">Delivery Rate Trend</h5>
                    <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                      <h1 className="display-3 text-primary fw-bold">94.7%</h1>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card className="border-0 shadow-sm rounded-3 h-100">
                  <Card.Body>
                    <h5 className="mb-4">Reply Rate Trend</h5>
                    <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                      <h1 className="display-3 text-success fw-bold">21.0%</h1>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Yeni Kampanya Modal */}
      <Modal 
        show={showNewCampaignModal} 
        onHide={() => setShowNewCampaignModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Campaign Name</Form.Label>
              <Form.Control type="text" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Template</Form.Label>
              <Form.Select>
                {dummyTemplates.map(template => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Recipients</Form.Label>
              <Form.Control type="number" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Schedule Date</Form.Label>
              <Form.Control type="datetime-local" />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewCampaignModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={() => setShowNewCampaignModal(false)}
            style={{
              background: 'linear-gradient(45deg, #25D366 30%, #128C7E 90%)',
              border: 'none',
            }}
          >
            Create Campaign
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Yeni Şablon Modal */}
      <Modal 
        show={showNewTemplateModal} 
        onHide={() => setShowNewTemplateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Template Name</Form.Label>
              <Form.Control type="text" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select>
                <option value="onboarding">Onboarding</option>
                <option value="update">Update</option>
                <option value="notification">Notification</option>
                <option value="support">Support</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Content</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={4} 
              />
              <Form.Text className="text-muted">
                Use {'{variable_name}'} for dynamic content
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewTemplateModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={() => setShowNewTemplateModal(false)}
            style={{
              background: 'linear-gradient(45deg, #25D366 30%, #128C7E 90%)',
              border: 'none',
            }}
          >
            Create Template
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default WhatsappMarketingPage;
